import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeferredLink } from "@/lib/services/linkService/linkService.types";
import { OS } from "@/lib/utils/getDeviceOs";
import {
  RedirectionUrls,
  RedirectStatus,
} from "@/components/redirectFlow/ClientRedirect.types";
import {
  getRedirectAttemptKey,
  hasRedirectBeenAttempted,
  markRedirectAttempted,
} from "@/lib/utils/redirectFlow/redirectAttempt";
import { resolveRedirectionUrl } from "@/lib/utils/redirectFlow/redirectDestination";
import { addNavigationListenersForRedirectingToWebFallback } from "@/lib/utils/redirectFlow/webFallback";
import {
  useAppBounce,
  useInAppEscape,
} from "@/lib/inAppEscape/InAppEscapeContext";
import { CreateClick } from "./useClickReporter";

// How long to wait after firing a programmatic store redirect before assuming
// the in-app browser blocked it and surfacing the manual store button instead.
const REDIRECT_FALLBACK_TIMEOUT_MS = 3_000;

type UseRedirectFlowParams = {
  os: OS | null;
  serverOs: "ios" | "android" | "web";
  deferredLink: DeferredLink;
  redirectionUrls: RedirectionUrls;
  createClick: CreateClick;
  playStoreUrlWithClickId: (clickId: string) => string | null;
};

/**
 * Owns the redirect itself: where the visitor goes, whether they have already
 * been sent there once, and how the attempt ended.
 */
export function useRedirectFlow({
  os,
  serverOs,
  deferredLink,
  redirectionUrls,
  createClick,
  playStoreUrlWithClickId,
}: UseRedirectFlowParams) {
  const [status, setStatus] = useState<RedirectStatus | null>(null);
  const hasAttemptedRedirect = useRef(false);
  const isRedirecting = useRef(false);
  const fallbackTimerRef = useRef<number | null>(null);
  const inAppEscape = useInAppEscape();
  const appBounce = useAppBounce();

  const redirectionUrl = useMemo(
    () =>
      os === null ? undefined : resolveRedirectionUrl(os, redirectionUrls),
    [os, redirectionUrls]
  );

  // No destination exists for this OS, so there is nothing to wait for — show
  // RedirectFailed rather than spin forever.
  const redirectStatus: RedirectStatus | null =
    status ?? (os !== null && redirectionUrl === undefined ? "failed" : null);

  // If the cookie exists on mobile, a redirect was already attempted and the
  // page reloaded (e.g. App Store not installed on iOS) — record this visit,
  // but do not auto-redirect again or the user can get stuck in a loop.
  // On web, https redirects always succeed so skip this check.
  const checkAlreadyRedirected = useCallback(() => {
    const redirectKey = getRedirectAttemptKey(deferredLink.id);
    if (serverOs !== "web" && hasRedirectBeenAttempted(redirectKey)) {
      createClick();
      setStatus("alreadyRedirected");
      return true;
    }
    return false;
  }, [serverOs, deferredLink.id, createClick]);

  const settle = useCallback((settledStatus: RedirectStatus) => {
    isRedirecting.current = false;
    setStatus(settledStatus);
  }, []);

  // In-app browsers (Messenger/Instagram/Facebook, etc.) silently ignore
  // programmatic navigation to the App Store / Play Store when there is no
  // user gesture, so the page never navigates away and the spinner would
  // spin forever. If we are still visible shortly after firing the redirect,
  // surface the manual store button — a real user tap does open the store.
  // Note: on iOS this only bites when copy-link is disabled; with it enabled
  // iOS renders the interactive GetAppWithLinkCopy UI instead of redirecting.
  const redirectWithFallback = useCallback(
    (finalUrl: string) => {
      if ((os === "ios" || os === "android") && redirectionUrls.web) {
        addNavigationListenersForRedirectingToWebFallback(redirectionUrls.web);
      }

      window.location.replace(finalUrl);

      if (serverOs === "web") return;
      fallbackTimerRef.current = window.setTimeout(() => {
        if (document.visibilityState === "hidden") {
          isRedirecting.current = false;
          return;
        }
        settle("alreadyRedirected");
      }, REDIRECT_FALLBACK_TIMEOUT_MS);
    },
    [os, serverOs, redirectionUrls.web, settle]
  );

  const attemptRedirect = useCallback(
    async (destination: string) => {
      if (checkAlreadyRedirected()) return;

      if (hasAttemptedRedirect.current) {
        if (!isRedirecting.current) {
          setStatus("failed");
        }
        return;
      }
      hasAttemptedRedirect.current = true;
      isRedirecting.current = true;

      // First, before the mark and the click. See useAppBounce.
      if (
        appBounce?.start(destination, {
          onSettled: () => settle("alreadyRedirected"),
          onWaitingForTap: () => settle("appOpenOnTap"),
          recordClick: createClick,
        })
      ) {
        return;
      }

      markRedirectAttempted(getRedirectAttemptKey(deferredLink.id));

      const clickId = await createClick();
      const finalUrl =
        clickId && destination === redirectionUrls.android
          ? (playStoreUrlWithClickId(clickId) ?? destination)
          : destination;

      // Only starts where location.replace below would land on a blank page.
      // The escape ends on the same already-redirected screen the 3s timeout
      // produces, so this is a different way of leaving, not a different flow.
      //
      // The web-fallback listeners are deliberately skipped here: a successful
      // escape hides this page, and they would read the visitor switching back
      // to Instagram as "app not installed" and send them to the web
      // destination out from under the store they were just handed.
      const hasEscaped = inAppEscape?.start(finalUrl, {
        clickId,
        onSettled: () => settle("alreadyRedirected"),
      });
      if (hasEscaped) return;

      redirectWithFallback(finalUrl);
    },
    [
      checkAlreadyRedirected,
      redirectionUrls.android,
      deferredLink.id,
      createClick,
      playStoreUrlWithClickId,
      inAppEscape,
      appBounce,
      settle,
      redirectWithFallback,
    ]
  );

  // Clear the pending in-app-browser fallback timer on unmount.
  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current !== null) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, []);

  return {
    redirectionUrl,
    redirectStatus,
    checkAlreadyRedirected,
    attemptRedirect,
  };
}
